import axios from "axios";
import jwtDecode from "jwt-decode";
import BugsnagManager from "../../BugsnagManager";
import reduxStore from "../../store/store";
import { setToast, logout } from "../../store/actions";
import {
  redactTelemetryData,
  sanitizeTelemetryUrl,
} from "../Utils/telemetryRedaction";
import { clearSsoContext } from "../ExternalReservationPages/ssoContext";
import { getSsoLogoutDestination } from "../ExternalReservationPages/ssoLogout";

const NUM_MINS_IN_DAY = 1440;

function shouldIgnoreNetworkError(error) {
  const url = error?.config?.url || "";
  const code = `${error?.response?.data?.code || -1}`;
  const status = error.response?.status || -1;
  const method = error.response?.config?.method;
  const message = error.message || "";
  const networkErrorMessage = error?.response?.data?.Message || "";
  let shouldIgnore = false;

  if (
    url.includes("/subscription/shop/locationRates") &&
    ["LAZPORTAL0516", "LAZPORTAL0800"].includes(code)
  ) {
    shouldIgnore = true;
  } else if (
    url.includes("/account/registration/login") &&
    status === 500 &&
    code === "9999"
  ) {
    shouldIgnore = true;
  } else if (message === "Network Error") {
    shouldIgnore = true;
  } else if (message === "timeout of 0ms exceeded") {
    // if a request has been cancelled (like multiple GET multiple rates calls on a map)
    shouldIgnore = true;
  } else if ([401, 403].includes(status)) {
    shouldIgnore = true;
  } else if (
    url.includes("/subscription/subscription/save") &&
    error?.validationErrors?.length
  ) {
    // if there were valid subscription errors, like invalid payment method
    shouldIgnore = true;
  } else if (
    url.includes("/Rate/GetRate") &&
    `${networkErrorMessage}` === "141"
  ) {
    // Parking end time cannot be less or equals current time
    shouldIgnore = true;
  } else if (
    url.includes("/subscription/subscription/save") &&
    `${code}` === "LAZPORTAL05335"
  ) {
    // Failed to activate as there is already an active subscription for the location
    shouldIgnore = true;
  } else if (
    url.includes("/subscription/account/payment/method/") &&
    method === "delete" &&
    `${code}` === "SUB3120"
  ) {
    // card is attached to a subscription
    shouldIgnore = true;
  } else if (
    url.includes("SpecialProgram/GetProgramEnrollments") &&
    `${code}` === "133"
  ) {
    // if an invalid email was used
    shouldIgnore = true;
  } else if (
    url.includes("/Reservations/GetReservationsInfoByAccountId") &&
    `${networkErrorMessage}` === "125"
  ) {
    // if an invalid email was used
    shouldIgnore = true;
  } else if (
    url.includes("ParkingLocation/GetLocationFields") &&
    `${code}` === "157"
  ) {
    shouldIgnore = true;
  }
  if (shouldIgnore) {
    console.warn("Ignoring an expected network error", {
      method,
      status,
      url: sanitizeTelemetryUrl(url.split("?")[0]),
    });
  }
  return shouldIgnore;
}

export class AxiosManager {
  constructor() {
    this.requestQueue = [];
    this.queueIsRunning = false;
    this.allowedStatuses = [400, 404, 408, 409, 422, 426, 502, 503];
    this.unauthenticatedStatuses = [401, 403];
    this.criticalStatuses = [500];

    const accessToken = this.getAccessToken();

    this.instance = axios.create({
      headers: {
        Authorization: accessToken ? `Bearer ${accessToken}` : "",
        Accept: "application/json",
        "X-Api-Key": process.env.REACT_APP_API_KEY,
        "X-Tenant": process.env.REACT_APP_TENANT,
      },
    });

    this.instance.interceptors.request.use(
      (config) => {
        if (config.skipAuth) {
          delete config.headers.Authorization;
          delete config.skipAuth;
        }
        if (!config.headers?.Authorization) {
          delete config.headers.Authorization;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        try {
          const rawUrl =
            error?.config?.url || error?.response?.config?.url || "";
          const url = sanitizeTelemetryUrl(rawUrl.split("?")[0]);
          const status = error?.response?.status;
          const method = String(
            error?.config?.method || error?.response?.config?.method || ""
          ).toUpperCase();

          if (url && !shouldIgnoreNetworkError(error)) {
            const outcome = status || "network error";
            BugsnagManager.notify(new Error(url + " returned " + outcome), {
              context: url + " returned " + outcome,
              metadata: redactTelemetryData({
                Request: {
                  method,
                  url,
                  timedOut: !!error?.response?.request?._timedOut,
                  aborted: !!error?.response?.request?._aborted,
                },
                Response: {
                  status: status || null,
                  hasBody: error?.response?.data != null,
                },
              }),
            });
          }
          return Promise.reject(error);
        } catch (e) {
          this.log("Could not handle axios error interceptor", e);
          BugsnagManager.notify(new Error("Network error reporting failed"), {
            context: "Could not handle axios error interceptor",
          });
          return Promise.reject(error);
        }
      }
    );
  }

  log = (message, error) => {
    // if (error) {
    //   console.warn(message, error);
    // } else {
    //   console.log(message);
    // }
    BugsnagManager.leaveBreadcrumb(message);
  };

  getRefreshToken = () => {
    return localStorage.getItem("refresh_token");
  };

  setRefreshToken = (token) => {
    if (token) {
      return localStorage.setItem("refresh_token", token);
    }
    return localStorage.removeItem("refresh_token");
  };

  getAccessToken = () => {
    return localStorage.getItem("access_token");
  };

  setAccessToken = (token) => {
    if (token) {
      this.instance.defaults.headers.Authorization = "Bearer " + token;
      return localStorage.setItem("access_token", token);
    } else {
      this.instance.defaults.headers.Authorization = "";
      return localStorage.removeItem("access_token");
    }
  };

  clearAuthTokens = () => {
    this.instance.defaults.headers.Authorization = "";
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
  };

  isSessionValid = () => {
    return !!this.getAccessToken();
  };

  // `redirect` defaults to true so the sign-out button and the session-expired
  // paths keep handing the user to the identity provider. The post-purchase
  // sign-out opts out: it has to tear the session down while leaving the
  // confirmation screen mounted, and navigating would unload the page before
  // the customer sees that the transaction succeeded.
  logout = (options = {}) => {
    const { reason, alertUser = false, redirect = true } = options;
    const dispatch = reduxStore.store.dispatch;
    const state = reduxStore.store.getState() || {};
    const destination = getSsoLogoutDestination({
      descriptor: state.user?.ssoAuth,
      isLoginViaNu: state.user?.isLoginViaNu,
      isLoginViaEmu: state.user?.isLoginViaEmu,
    });

    this.emptyQueue();
    this.clearAuthTokens();
    clearSsoContext();
    if (alertUser) {
      dispatch(
        setToast({
          message: "For security purposes, you have been signed out.",
          type: "E",
        })
      );
    }

    this.log(reason ? "Logging out with a recorded reason" : "Logging out");
    dispatch(logout());
    if (redirect) {
      window.location.replace(destination);
    }
  };

  checkTokenExpiration(token) {
    if (!token) return false;
    try {
      const decoded = jwtDecode(token);
      // If there's no exp claim, assume it's expired
      if (!decoded.exp) return true;
      const isExpired = decoded.exp * 1000 < Date.now();

      if (isExpired) {
        this.clearAuthTokens();
      }
      return isExpired;
    } catch (error) {
      console.warn("Unable to validate the supplied session token");
      return true;
    }
  }

  refreshToken = async () => {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.log("Attempted to refresh token with no refresh_token");
      return false;
    }
    const decodedToken = jwtDecode(refreshToken);
    const exp = decodedToken.exp * 1000;
    const minuteDiff = Math.floor(
      ((exp - Date.now()) / 1000 / 60 / 60 / 24) * NUM_MINS_IN_DAY
    );
    if (minuteDiff <= 0) {
      const expDate = new Date(exp);
      this.log(
        `Refresh token expired ${expDate.toLocaleDateString()} at ${expDate.toLocaleTimeString()}`
      );
      return false;
    } else {
      this.log(`Refresh token expires in ${minuteDiff} minutes(s)`);
    }
    let response;
    try {
      response = await axios.post(
        process.env.REACT_APP_GRS_URL +
          "/rest/account/registration/token/refresh",
        {
          refresh_token: refreshToken,
        },
        {
          headers: {
            "X-Api-Key": process.env.REACT_APP_API_KEY,
            "X-Tenant": process.env.REACT_APP_TENANT,
          },
        }
      );
    } catch (error) {
      BugsnagManager.notify(new Error("Unexpected response in user refresh"), {
        context: "Unexpected response in user refresh",
      });
    }
    if (response?.data?.access_token) {
      this.setAccessToken(response.data.access_token);
      this.setRefreshToken(response.data.refresh_token); // probably not needed
      this.log("JWT was refreshed");
      return response.data.access_token;
    } else {
      this.log("JWT was not refreshed");
    }
    return false;
  };

  emptyQueue = () => {
    this.requestQueue = [];
    this.queueIsRunning = false;
  };

  enqueue = (request, config) => {
    const promise = new Promise((resolve, reject) => {
      this.requestQueue.push({ request, config, resolve, reject });
    });
    this.dequeue();
    return promise;
  };

  async dequeue() {
    if (this.queueIsRunning) {
      return false;
    }
    const item = this.requestQueue.shift();
    if (!item) {
      return false;
    }
    try {
      this.queueIsRunning = true;
      let shouldContinue = true;
      if (
        this.instance?.defaults?.headers?.Authorization &&
        !item.config?.skipAuth
      ) {
        let decodedJwt = null;
        try {
          decodedJwt = jwtDecode(
            this.instance?.defaults?.headers?.Authorization.replace(
              "Bearer ",
              ""
            )
          );
        } catch (error) {
          this.log("Unable to decode jwt", error);
          BugsnagManager.notify(new Error("Unable to decode the current JWT"), {
            context: "Unable to decode jwt",
          });
          this.logout({ reason: "Unable to decode jwt", alertUser: true });
          shouldContinue = false;
          return false;
        }
        if (decodedJwt?.exp) {
          const exp = decodedJwt.exp * 1000;
          const minuteDiff = Math.floor(
            ((exp - Date.now()) / 1000 / 60 / 60 / 24) * NUM_MINS_IN_DAY
          );
          if (minuteDiff <= 0) {
            const expDate = new Date(exp);
            this.log(
              `JWT expired ${expDate.toLocaleDateString()} at ${expDate.toLocaleTimeString()}`
            );
            const response = await this.refreshToken();
            if (!response) {
              this.logout({
                reason: "Unexpected response in user refresh",
                alertUser: true,
              });
              shouldContinue = false;
              return false;
            }
          } else {
            this.log(`JWT expires in ${minuteDiff} minutes(s)`);
          }
        } else {
          this.log("Exp property not found on decodedJwt");
        }
      }
      if (shouldContinue) {
        item
          .request()
          .then((response) => {
            item.resolve(response);
          })
          .catch((error) => {
            item.reject(error);
          });
      }
    } catch (error) {
      item.reject(error);
    }
    this.queueIsRunning = false;
    this.dequeue();
    return true;
  }

  convertParams = (params) => {
    return {
      url: params[0],
      skipAuth: params.some((param) => param?.skipAuth),
    };
  };

  get = (...params) => {
    return this.enqueue(() => {
      return this.instance.get(...params);
    }, this.convertParams(params));
  };

  put = (...params) => {
    return this.enqueue(() => {
      return this.instance.put(...params);
    }, this.convertParams(params));
  };

  patch = (...params) => {
    return this.enqueue(() => {
      return this.instance.patch(...params);
    }, this.convertParams(params));
  };

  post = (...params) => {
    return this.enqueue(() => {
      return this.instance.post(...params);
    }, this.convertParams(params));
  };

  del = (...params) => {
    return this.enqueue(() => {
      return this.instance.delete(...params);
    }, this.convertParams(params));
  };
}

export default new AxiosManager();
